import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from '../components/CTAButton';
import { FAQItem } from '../components/FAQItem';
import { Modal } from '../components/Modal';
import { PaymentMethods } from '../components/PaymentMethods';
import { CheckCircle2, XCircle, Activity, BookOpen, ShoppingCart, Pill, ShieldCheck, Clock, Heart, Star } from 'lucide-react';
import { initMetaPixel, trackAddToCart } from '../utils/metaPixel';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function LandingPage() {
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  useEffect(() => {
    // Initialize Meta Pixel and track PageView on component mount
    initMetaPixel();
  }, []);
  const handleCTAClick = () => {
    // Track AddToCart event when user clicks CTA button
    trackAddToCart(21900, 'COP');
  };
  return <div className="min-h-screen bg-dark font-sans text-cream overflow-x-hidden" data-id="element-46">
      {/* ANNOUNCEMENT BAR */}
      <div className="bg-coral text-white py-2.5 px-4 text-center text-sm font-medium tracking-wide shadow-lg fixed top-0 left-0 right-0 z-[9999]" data-id="element-47">
        <div className="flex items-center justify-center space-x-2 max-w-7xl mx-auto" data-id="element-48">
          <div className="flex items-center" data-id="element-49">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current text-yellow-300 mr-0.5" data-id="element-50" />)}
          </div>
          <span className="opacity-90" data-id="element-51">
            <strong className="font-bold" data-id="element-52">4.9/5</strong> • Más de{' '}
            <strong className="font-bold" data-id="element-53">12,450</strong> mujeres ya
            transformaron su metabolismo
          </span>
        </div>
      </div>

      {/* HERO SECTION */}
      <section className="relative pt-20 pb-24 md:pt-28 md:pb-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-54">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="text-center max-w-4xl mx-auto" data-id="element-55">
          <motion.div variants={fadeInUp} className="mb-6 inline-flex items-center px-4 py-2 rounded-full bg-sage-dark text-sage-light font-medium text-sm tracking-wide uppercase" data-id="element-56">
            <span className="flex h-2 w-2 rounded-full bg-sage-light mr-2" data-id="element-57"></span>
            Para mujeres de 35 a 55 años
          </motion.div>

          <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-cream leading-tight mb-6" data-id="element-58">
            Protocolo Adiós Barriga Hormonal en 21D
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-xl md:text-2xl text-cream/80 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-59">
            Deja de contar calorías y empieza a sanar tus hormonas. El reset
            biológico de 21 días diseñado para eliminar la hinchazón, el
            cansancio y la "barriga de estrés" sin dietas restrictivas.
          </motion.p>

          <motion.div variants={fadeInUp} className="mb-10 max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-2xl border-4 border-sage-dark/30" data-id="element-60">
            <video className="w-full h-auto object-cover" autoPlay muted loop playsInline poster="https://cdn.magicpatterns.com/uploads/q14yzCfHheh3EgsAGmTRvk/Captura_de_pantalla_2026-03-25_a_la(s)_9.52.23_p.m..png" data-id="element-61">
              <source src="https://cdn.magicpatterns.com/uploads/aVXPHVf9t58WKNj6Yj6xg5/Woman_fitting_jeans_202603252151.mp4" type="video/mp4" data-id="element-62" />
              Tu navegador no soporta el elemento de video.
            </video>
          </motion.div>

          <motion.div variants={fadeInUp} className="flex flex-col items-center" data-id="element-63">
            <CTAButton href="#pricing" className="w-full sm:w-auto text-lg px-10 py-5 mb-2" data-id="element-64">
              <span data-id="element-65">¡SÍ, QUIERO EMPEZAR MI RESET AHORA MISMO!</span>
              <span className="text-sm font-normal opacity-90 mt-1" data-id="element-66">
                por tan sólo $21.900 COP
              </span>
            </CTAButton>

            <p className="text-sm text-coral font-semibold mb-3 animate-pulse" data-id="element-67">
              ⏰ Precio especial por tiempo limitado
            </p>

            <PaymentMethods data-id="element-68" />
          </motion.div>
        </motion.div>
      </section>

      {/* HOOK & PROBLEM SECTION */}
      <section className="py-20 bg-dark-muted px-4 sm:px-6 lg:px-8" data-id="element-69">
        <div className="max-w-7xl mx-auto" data-id="element-70">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-71">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-72">
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif font-bold mb-6 text-cream" data-id="element-73">
                ¿Sientes que tu cuerpo ya no te escucha?
              </motion.h2>

              <div className="space-y-6 text-lg text-cream/80 leading-relaxed" data-id="element-74">
                <motion.p variants={fadeInUp} data-id="element-75">
                  Te despiertas cansada, te alimentas "bien", haces ejercicio,
                  pero tu abdomen sigue ahí, inflamado, como si tuviera vida
                  propia.
                </motion.p>
                <motion.p variants={fadeInUp} className="font-medium text-coral" data-id="element-76">
                  No es tu falta de voluntad. No es la edad. Es tu metabolismo
                  hormonal enviando una señal de auxilio que las dietas
                  tradicionales de gimnasio simplemente no saben resolver.
                </motion.p>
                <motion.p variants={fadeInUp} data-id="element-77">
                  Después de los 35 años, las reglas del juego cambian. La caída
                  del estrógeno y el aumento del cortisol (la hormona del
                  estrés) le dan a tu cuerpo una orden muy específica:{' '}
                  <strong className="text-cream" data-id="element-78">
                    "Guarda grasa en el abdomen para protegernos"
                  </strong>
                  .
                </motion.p>
                <motion.p variants={fadeInUp} data-id="element-79">
                  Por eso, ese pantalón "de la suerte" ya no cierra. Por eso
                  sientes esa pesadez después de cada comida y esa neblina
                  mental que te quita la agudeza en el trabajo. Tu cuerpo está
                  en estado de alerta, y mientras esté inflamado, no soltará esa
                  barriga por más ensaladas que comas.
                </motion.p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-80">
              <div className="absolute inset-0 bg-sage-light rounded-2xl transform translate-x-4 translate-y-4" data-id="element-81"></div>
              <img src="https://cdn.magicpatterns.com/uploads/jZD9iERgb5qZMKnrtzoRPs/REEMPLAZA_con_el_202603251346.jpg" alt="Transformación antes y después del protocolo" className="relative rounded-2xl shadow-xl w-full object-cover z-10" data-id="element-82" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY IT DIDN'T WORK SECTION */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-dark" data-id="element-83">
        <div className="max-w-7xl mx-auto" data-id="element-84">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-85">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-cream" data-id="element-86">
              Por qué lo que has intentado no funciona
            </h2>
            <p className="text-lg text-cream/80 max-w-2xl mx-auto" data-id="element-87">
              Las estrategias convencionales están diseñadas para metabolismos
              de 20 años, no para el delicado equilibrio hormonal después de los
              35.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-88">
            {[{
            title: 'El cardio excesivo',
            desc: 'Correr o hacer spinning intenso dispara tu cortisol, obligando a tu cuerpo a guardar MÁS grasa abdominal como reserva de emergencia.'
          }, {
            title: 'Restricción calórica',
            desc: "Comer 1,200 calorías pone a tu metabolismo en 'modo ahorro', bloqueando la quema de grasa por meses."
          }, {
            title: "Productos 'Light'",
            desc: "Los procesados 'fitness' destruyen tu flora intestinal y empeoran la inflamación que intentas combatir."
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2,
            duration: 0.5
          }} className="bg-dark-light p-8 rounded-2xl shadow-soft border border-cream-dark flex flex-col items-center text-center" data-id="element-89">
                <div className="w-12 h-12 bg-coral-light rounded-full flex items-center justify-center mb-6 text-coral" data-id="element-90">
                  <XCircle className="w-6 h-6" data-id="element-91" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-cream" data-id="element-92">
                  {item.title}
                </h3>
                <p className="text-cream/80 leading-relaxed" data-id="element-93">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-sage text-white relative overflow-hidden" data-id="element-94">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-white opacity-5 rounded-full blur-3xl" data-id="element-95"></div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-white opacity-5 rounded-full blur-3xl" data-id="element-96"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-97">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-98">
            <motion.div variants={fadeInUp} className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-8 backdrop-blur-sm" data-id="element-99">
              <Activity className="w-8 h-8 text-white" data-id="element-100" />
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif font-bold mb-6" data-id="element-101">
              El Reset de Cortisol y Glucosa 40+
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl leading-relaxed text-sage-light mb-8" data-id="element-102">
              Este no es un plan de pérdida de peso tradicional; es un{' '}
              <strong className="text-white" data-id="element-103">
                protocolo de desinflamación celular
              </strong>
              . A través de la Crononutrición y el Orden Específico de
              Ingestión, logramos que tu insulina permanezca estable y tu
              cortisol baje.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-2xl font-serif italic text-white" data-id="element-104">
              "Cuando tus hormonas se calman, tu abdomen se desinflama de forma
              natural y automática."
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-dark-muted" data-id="element-105">
        <div className="max-w-7xl mx-auto" data-id="element-106">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-107">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-cream" data-id="element-108">
              Lo que recibes hoy
            </h2>
            <p className="text-lg text-cream/80 max-w-2xl mx-auto" data-id="element-109">
              Todo lo que necesitas para implementar el protocolo desde casa,
              sin complicaciones.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-110">
            {[{
            icon: <BookOpen className="w-6 h-6" data-id="element-111" />,
            title: 'Guía Principal: Protocolo Adiós Barriga Hormonal en 21D (PDF)',
            desc: 'El mapa exacto de las 3 fases (Choque, Sello y Consolidación).'
          }, {
            icon: <Clock className="w-6 h-6" data-id="element-112" />,
            title: 'El Método del Orden de Ingestión',
            desc: 'Cómo comer lo que te gusta (carbohidratos incluidos) sin disparar la insulina.'
          }, {
            icon: <ShoppingCart className="w-6 h-6" data-id="element-113" />,
            title: 'Lista de Compras 2026/2027',
            desc: 'Los alimentos exactos que debes buscar en el supermercado para sanar tu intestino.'
          }, {
            icon: <Pill className="w-6 h-6" data-id="element-114" />,
            title: 'Guía de Suplementación Natural',
            desc: 'Qué magnesio comprar, qué vitaminas usar y cómo tomarlas para ver resultados en la primera semana.'
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: index % 2 === 0 ? -30 : 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="flex items-start p-6 bg-dark-light rounded-2xl border border-sage-dark" data-id="element-115">
                <div className="flex-shrink-0 w-12 h-12 bg-sage-dark text-sage-light rounded-full flex items-center justify-center mr-6" data-id="element-116">
                  {item.icon}
                </div>
                <div data-id="element-117">
                  <h3 className="text-xl font-bold mb-2 text-cream" data-id="element-118">
                    {item.title}
                  </h3>
                  <p className="text-cream/80" data-id="element-119">{item.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR / NOT FOR */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-dark" data-id="element-120">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-121">
          {/* For */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-dark-light p-8 md:p-10 rounded-3xl shadow-soft border border-sage-dark" data-id="element-122">
            <h3 className="text-2xl font-serif font-bold mb-8 text-cream flex items-center" data-id="element-123">
              <Heart className="w-6 h-6 text-sage-light mr-3" data-id="element-124" />
              Este protocolo ES para ti si:
            </h3>
            <ul className="space-y-6" data-id="element-125">
              {["Mujeres entre 35 y 55 años que notan un aumento de volumen abdominal que 'no se mueve' con nada.", "Profesionales que sufren de fatiga por la tarde y 'niebla mental'.", 'Mujeres en perimenopausia que quieren recuperar su figura y su energía vital.'].map((text, i) => <li key={i} className="flex items-start" data-id="element-126">
                  <CheckCircle2 className="w-6 h-6 text-sage-light mr-4 flex-shrink-0 mt-1" data-id="element-127" />
                  <span className="text-cream/80 text-lg" data-id="element-128">{text}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Not For */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-dark-light p-8 md:p-10 rounded-3xl shadow-soft border border-sage-dark opacity-90" data-id="element-129">
            <h3 className="text-2xl font-serif font-bold mb-8 text-cream flex items-center" data-id="element-130">
              <XCircle className="w-6 h-6 text-coral mr-3" data-id="element-131" />
              Este protocolo NO es para ti si:
            </h3>
            <ul className="space-y-6" data-id="element-132">
              {["Personas que buscan una 'pastilla mágica' sin cambiar el orden de sus comidas.", 'Hombres o adolescentes (esta es una arquitectura de hormonas femeninas).', 'Personas que no están dispuestas a leer un protocolo serio y estructurado.'].map((text, i) => <li key={i} className="flex items-start" data-id="element-133">
                  <div className="w-6 h-6 rounded-full bg-dark-muted flex items-center justify-center mr-4 flex-shrink-0 mt-1" data-id="element-134">
                    <div className="w-2 h-2 bg-cream/40 rounded-full" data-id="element-135"></div>
                  </div>
                  <span className="text-cream/60 text-lg" data-id="element-136">{text}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS & PROOF */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-dark-muted" data-id="element-137">
        <div className="max-w-7xl mx-auto" data-id="element-138">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-139">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-140">
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif font-bold mb-8 text-cream text-center lg:text-left" data-id="element-141">
                ¿Cómo funciona?
              </motion.h2>

              <div className="space-y-8" data-id="element-142">
                {[{
                step: '1',
                title: 'Acceso Inmediato',
                desc: 'Recibes el protocolo en tu correo segundos después de la compra.'
              }, {
                step: '2',
                title: 'Aplica el Reset',
                desc: 'Sigues la lista de alimentos y el orden de comidas por 21 días.'
              }, {
                step: '3',
                title: 'Recupera tu Ropa',
                desc: 'Nota cómo la inflamación desaparece y tu energía regresa desde la primera semana.'
              }].map((item, i) => <motion.div variants={fadeInUp} key={i} className="flex" data-id="element-143">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-sage-light text-sage font-bold flex items-center justify-center mr-6" data-id="element-144">
                      {item.step}
                    </div>
                    <div data-id="element-145">
                      <h4 className="text-xl font-bold text-cream mb-2" data-id="element-146">
                        {item.title}
                      </h4>
                      <p className="text-cream/80" data-id="element-147">{item.desc}</p>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-dark-light p-8 md:p-10 rounded-3xl border border-sage-dark relative" data-id="element-148">
              <div className="absolute -top-5 -right-5 w-20 h-20 bg-coral rounded-full flex items-center justify-center text-white transform rotate-12 shadow-lg" data-id="element-149">
                <span className="font-bold text-center leading-tight text-sm" data-id="element-150">
                  Ciencia
                  <br data-id="element-151" />
                  Aplicada
                </span>
              </div>
              <h3 className="text-2xl font-serif font-bold mb-6 text-cream" data-id="element-152">
                No es magia, es ciencia.
              </h3>
              <p className="text-lg text-cream/80 mb-6 leading-relaxed" data-id="element-153">
                Este protocolo se basa en biología aplicada. Al estabilizar la
                glucosa, detienes la producción de grasa visceral.
              </p>
              <div className="bg-dark-muted p-6 rounded-2xl border-l-4 border-sage-light" data-id="element-154">
                <p className="italic text-cream font-medium" data-id="element-155">
                  "En los primeros 7 días, la mayoría de nuestras lectoras
                  reportan haber bajado entre 3 y 5 centímetros de contorno
                  abdominal simplemente apagando la señal de inflamación."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-dark" data-id="element-156">
        <div className="max-w-3xl mx-auto" data-id="element-157">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-158">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-cream" data-id="element-159">
              Preguntas Frecuentes
            </h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-dark-light rounded-3xl p-6 md:p-10 shadow-soft border border-sage-dark" data-id="element-160">
            <FAQItem question="¿Tengo que cocinar cosas raras?" answer="No, usas comida real que encuentras en cualquier mercado de Colombia." data-id="element-161" />
            <FAQItem question="¿Es seguro si tengo hipotiroidismo?" answer="Sí, de hecho, bajar la inflamación ayuda notablemente al eje tiroideo." data-id="element-162" />
            <FAQItem question="¿Cuánto tiempo tengo acceso?" answer="El acceso es de por vida, incluye futuras actualizaciones de la edición 2026." data-id="element-163" />
            <FAQItem question="¿Hay garantía?" answer="Sí, tienes 7 días de garantía total a través de la plataforma Hotmart. Si no es para ti, te devolvemos el dinero sin preguntas." data-id="element-164" />
          </motion.div>
        </div>
      </section>

      {/* PRICING & FINAL CTA */}
      <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8 bg-dark-muted relative overflow-hidden" data-id="element-165">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-full bg-sage-dark/20 rounded-full blur-3xl -z-10" data-id="element-166"></div>

        <div className="max-w-6xl mx-auto" data-id="element-167">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-168">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif font-bold mb-4 text-cream text-center" data-id="element-169">
              Tu inversión en salud
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-cream/80 mb-12 max-w-2xl mx-auto text-center" data-id="element-170">
              Hoy puedes obtener el Protocolo Adiós Barriga Hormonal en 21D
              completo con un descuento especial
            </motion.p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-171">
              {/* Product Mockup */}
              <motion.div variants={fadeInUp} className="relative" data-id="element-172">
                <div className="absolute -top-6 -right-6 bg-coral text-white px-6 py-3 rounded-full font-bold text-lg shadow-xl z-10 transform rotate-12" data-id="element-173">
                  61% OFF
                </div>
                <img src="https://cdn.magicpatterns.com/uploads/iYagVYJZbBrdQqBf1Jm1Rk/Digital_product_bundle_202603251416.jpg" alt="Protocolo Adiós Barriga Hormonal - Paquete Digital Completo" className="rounded-2xl shadow-2xl w-full" data-id="element-174" />
              </motion.div>

              {/* Pricing Card */}
              <motion.div variants={fadeInUp} className="bg-dark-light rounded-3xl shadow-2xl p-8 md:p-12 border border-sage-dark relative overflow-hidden" data-id="element-175">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-sage-light via-coral to-sage-light" data-id="element-176"></div>

                {/* Comparison Price */}
                <div className="mb-8" data-id="element-177">
                  <div className="flex items-center justify-center mb-4" data-id="element-178">
                    <span className="text-2xl text-cream/40 line-through mr-3" data-id="element-179">
                      $56.000
                    </span>
                    <span className="bg-coral/20 text-coral px-4 py-2 rounded-full font-bold text-sm border border-coral/30" data-id="element-180">
                      AHORRA $34.100
                    </span>
                  </div>

                  <div className="flex justify-center items-baseline mb-2" data-id="element-181">
                    <span className="text-5xl md:text-6xl font-bold text-cream tracking-tight" data-id="element-182">
                      $21.900
                    </span>
                    <span className="text-2xl text-cream/80 ml-2 font-medium" data-id="element-183">
                      COP
                    </span>
                  </div>

                  <p className="text-center text-sage-light font-medium" data-id="element-184">
                    Precio especial de lanzamiento
                  </p>
                </div>

                <div className="bg-dark-muted rounded-2xl p-6 mb-8 border border-sage-dark" data-id="element-185">
                  <h4 className="font-bold text-cream mb-4 text-lg" data-id="element-186">
                    Incluye todo esto:
                  </h4>
                  <ul className="space-y-3 text-left" data-id="element-187">
                    {['Protocolo completo en PDF (21 días)', 'Método del Orden de Ingestión', 'Lista de Compras 2026/2027', 'Guía de Suplementación Natural', 'Acceso de por vida + actualizaciones', 'Garantía de 7 días'].map((item, i) => <li key={i} className="flex items-start text-cream/80" data-id="element-188">
                        <CheckCircle2 className="w-5 h-5 text-sage-light mr-3 flex-shrink-0 mt-0.5" data-id="element-189" />
                        <span data-id="element-190">{item}</span>
                      </li>)}
                  </ul>
                </div>

                <CTAButton className="w-full text-lg px-8 py-5 shadow-xl shadow-coral/20 mb-2" onClick={handleCTAClick} href="https://pay.hotmart.com/B105076399C" data-id="element-191">
                  <span data-id="element-192">DESCARGAR PROTOCOLO</span>
                  <span className="text-sm font-normal opacity-90 mt-1" data-id="element-193">
                    por tan sólo $21.900 COP
                  </span>
                </CTAButton>

                <p className="text-sm text-coral font-semibold mb-3 text-center animate-pulse" data-id="element-194">
                  ⏰ Precio especial por tiempo limitado
                </p>

                <PaymentMethods data-id="element-195" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-dark text-cream/60 py-12 text-center text-sm border-t border-sage-dark" data-id="element-196">
        <div className="max-w-7xl mx-auto px-4" data-id="element-197">
          <div className="flex justify-center space-x-6 mb-6" data-id="element-198">
            <button onClick={() => setIsPrivacyOpen(true)} className="hover:text-coral transition-colors" data-id="element-199">
              Política de Privacidad
            </button>
            <button onClick={() => setIsTermsOpen(true)} className="hover:text-coral transition-colors" data-id="element-200">
              Términos y Condiciones
            </button>
          </div>
          <p className="mb-4" data-id="element-201">
            © 2026 Protocolo Adiós Barriga Hormonal. Todos los derechos
            reservados.
          </p>
          <p className="max-w-2xl mx-auto text-xs opacity-50" data-id="element-202">
            Este producto no sustituye el consejo médico profesional. Consulte a
            su médico antes de comenzar cualquier programa de salud. Los
            resultados pueden variar de persona a persona.
          </p>
        </div>
      </footer>

      {/* MODALS */}
      <Modal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} title="Política de Privacidad" data-id="element-203">
        <div className="space-y-4" data-id="element-204">
          <p data-id="element-205">Última actualización: 25 de Marzo de 2026</p>
          <h3 className="text-xl font-bold text-cream mt-6 mb-2" data-id="element-206">
            1. Información que recopilamos
          </h3>
          <p data-id="element-207">
            Recopilamos la información que usted nos proporciona directamente
            cuando realiza una compra, se suscribe a nuestro boletín o se
            comunica con nosotros. Esta información puede incluir su nombre,
            dirección de correo electrónico y detalles de pago (procesados de
            forma segura a través de Hotmart).
          </p>

          <h3 className="text-xl font-bold text-cream mt-6 mb-2" data-id="element-208">
            2. Uso de la información
          </h3>
          <p data-id="element-209">
            Utilizamos la información que recopilamos para procesar sus
            transacciones, enviarle el producto digital adquirido, comunicarnos
            con usted sobre actualizaciones del producto y enviarle información
            relevante sobre salud hormonal (si ha optado por recibirla).
          </p>

          <h3 className="text-xl font-bold text-cream mt-6 mb-2" data-id="element-210">
            3. Protección de datos
          </h3>
          <p data-id="element-211">
            Implementamos medidas de seguridad diseñadas para proteger su
            información personal. Sus datos de pago son procesados
            exclusivamente por Hotmart, una plataforma segura y encriptada.
            Nosotros no almacenamos la información de su tarjeta de crédito.
          </p>

          <h3 className="text-xl font-bold text-cream mt-6 mb-2" data-id="element-212">
            4. Cookies y tecnologías de seguimiento
          </h3>
          <p data-id="element-213">
            Utilizamos cookies y tecnologías similares (como el Meta Pixel) para
            analizar el tráfico de nuestro sitio web, personalizar el contenido
            y optimizar nuestras campañas publicitarias. Usted puede configurar
            su navegador para rechazar todas las cookies.
          </p>
        </div>
      </Modal>

      <Modal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} title="Términos y Condiciones" data-id="element-214">
        <div className="space-y-4" data-id="element-215">
          <p data-id="element-216">Última actualización: 25 de Marzo de 2026</p>
          <h3 className="text-xl font-bold text-cream mt-6 mb-2" data-id="element-217">
            1. Naturaleza del Producto
          </h3>
          <p data-id="element-218">
            El "Protocolo Adiós Barriga Hormonal en 21D" es un producto digital
            informativo (PDF) diseñado para proporcionar educación general sobre
            nutrición y salud hormonal. No constituye consejo médico,
            diagnóstico ni tratamiento.
          </p>

          <h3 className="text-xl font-bold text-cream mt-6 mb-2" data-id="element-219">
            2. Descargo de Responsabilidad Médica
          </h3>
          <p data-id="element-220">
            La información contenida en este protocolo no debe utilizarse como
            sustituto del consejo de un profesional de la salud calificado.
            Siempre consulte a su médico antes de comenzar cualquier programa de
            dieta, suplementación o ejercicio, especialmente si tiene
            condiciones médicas preexistentes o toma medicamentos.
          </p>

          <h3 className="text-xl font-bold text-cream mt-6 mb-2" data-id="element-221">
            3. Propiedad Intelectual
          </h3>
          <p data-id="element-222">
            Todo el contenido del protocolo es propiedad exclusiva de sus
            creadores y está protegido por las leyes de derechos de autor. La
            compra le otorga una licencia personal, no transferible, para uso
            individual. Queda estrictamente prohibida la reproducción,
            distribución, reventa o modificación del material sin autorización
            expresa por escrito.
          </p>

          <h3 className="text-xl font-bold text-cream mt-6 mb-2" data-id="element-223">
            4. Política de Reembolso
          </h3>
          <p data-id="element-224">
            Ofrecemos una garantía de satisfacción de 7 días procesada a través
            de la plataforma Hotmart. Si no está satisfecho con el producto,
            puede solicitar un reembolso completo dentro de los 7 días
            posteriores a la compra directamente desde su cuenta de Hotmart.
          </p>
        </div>
      </Modal>
    </div>;
}